// src/components/roads/page/AiMlIcons.js
// --- UPDATE: v5.0 — FlowSense AI/ML icon system
// Custom 24×24 stroke icons (inherit currentColor, scale with boxSize) plus small
// visual primitives that mark AI/ML-driven UI consistently across the page:
//   <AiChip kind="ml" />            gradient pill: AI · ML · LIVE · RSF …
//   <AiGlyph icon={...} />          gradient icon tile for headers / KPI tiles
//   <ModelTag model="rsf" />        names the model behind a number
//   <ConfidenceMeter value={0.82}/> compact confidence bar
import React, { memo } from 'react';
import { Box, HStack, Icon, Text, Tooltip, createIcon, useColorModeValue } from '@chakra-ui/react';

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };

/** Neural network — three layers of nodes */
export const AiNeuralIcon = createIcon({
    displayName: 'AiNeuralIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <path d="M5 6l7 0M5 6l7 6M5 18l7-6M5 18l7 0M12 6l7 6M12 12l7 0M12 18l7-6" opacity="0.55" />
            <circle cx="5" cy="6" r="2" fill="currentColor" stroke="none" />
            <circle cx="5" cy="18" r="2" fill="currentColor" stroke="none" />
            <circle cx="12" cy="6" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="18" r="2" />
            <circle cx="19" cy="12" r="2.4" fill="currentColor" stroke="none" />
        </g>
    ),
});

/** AI processor chip with a spark in the core */
export const AiChipIcon = createIcon({
    displayName: 'AiChipIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <rect x="6" y="6" width="12" height="12" rx="2.5" />
            <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
            <path d="M12 8.6l.9 2.5 2.5.9-2.5.9-.9 2.5-.9-2.5-2.5-.9 2.5-.9z" fill="currentColor" stroke="none" />
        </g>
    ),
});

/** ML model — stacked layers */
export const MlModelIcon = createIcon({
    displayName: 'MlModelIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <path d="M12 3l8.5 4.5L12 12 3.5 7.5z" />
            <path d="M3.5 12L12 16.5 20.5 12" />
            <path d="M3.5 16.5L12 21l8.5-4.5" />
        </g>
    ),
});

/** Forecast — trend line extending into a dashed prediction */
export const AiForecastIcon = createIcon({
    displayName: 'AiForecastIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <path d="M3 20h18" opacity="0.5" />
            <path d="M3 16l4-4 3 2 4-5" />
            <path d="M14 9l3-2.5 4-1.5" strokeDasharray="2 2.2" />
            <circle cx="14" cy="9" r="1.6" fill="currentColor" stroke="none" />
            <path d="M18.5 13.5l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z" fill="currentColor" stroke="none" />
        </g>
    ),
});

/** Anomaly — signal with a spike and alert dot */
export const AiAnomalyIcon = createIcon({
    displayName: 'AiAnomalyIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <path d="M2 14h4l2-3 2.5 7L14 4l2.5 10H22" />
            <circle cx="14" cy="4" r="1.8" fill="currentColor" stroke="none" />
        </g>
    ),
});

/** Clustering — points grouped into two clusters */
export const AiClusterIcon = createIcon({
    displayName: 'AiClusterIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <circle cx="8" cy="9" r="5" strokeDasharray="2.5 2" />
            <circle cx="16.5" cy="15.5" r="4.5" strokeDasharray="2.5 2" />
            <circle cx="7" cy="8" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="9.5" cy="10" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="6.8" cy="11" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="16" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="18" cy="16.5" r="1.1" fill="currentColor" stroke="none" />
        </g>
    ),
});

/** Remaining useful life — gauge with hourglass needle */
export const RulGaugeIcon = createIcon({
    displayName: 'RulGaugeIcon',
    viewBox: '0 0 24 24',
    path: (
        <g {...stroke}>
            <path d="M4 17a8 8 0 1116 0" />
            <path d="M12 17l4-5" />
            <circle cx="12" cy="17" r="1.6" fill="currentColor" stroke="none" />
            <path d="M6.5 13.5l1 .6M17.5 13.5l-1 .6M12 9v1.2" />
        </g>
    ),
});

/** Sparkles — generic AI-assisted action */
export const AiSparkleIcon = createIcon({
    displayName: 'AiSparkleIcon',
    viewBox: '0 0 24 24',
    path: (
        <g fill="currentColor">
            <path d="M10 3l1.8 5.2L17 10l-5.2 1.8L10 17l-1.8-5.2L3 10l5.2-1.8z" />
            <path d="M18 14l.9 2.1 2.1.9-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z" opacity="0.75" />
        </g>
    ),
});

// ─────────────────────────────────────────────────────────────────────────────
// Visual primitives
// ─────────────────────────────────────────────────────────────────────────────
const CHIP_KINDS = {
    ai:      { label: 'AI',      gradient: 'linear(to-r, purple.500, pink.500)', icon: AiSparkleIcon },
    ml:      { label: 'ML',      gradient: 'linear(to-r, blue.500, cyan.500)',   icon: MlModelIcon },
    live:    { label: 'LIVE AI', gradient: 'linear(to-r, pink.500, red.400)',    icon: AiNeuralIcon },
    forecast:{ label: 'FORECAST',gradient: 'linear(to-r, purple.500, blue.500)', icon: AiForecastIcon },
    anomaly: { label: 'ANOMALY', gradient: 'linear(to-r, orange.400, red.500)',  icon: AiAnomalyIcon },
};

/** Small gradient pill marking AI/ML-derived content. */
export const AiChip = memo(({ kind = 'ai', label, tooltip, size = 'sm' }) => {
    const cfg = CHIP_KINDS[kind] || CHIP_KINDS.ai;
    const chip = (
        <HStack
            as="span"
            spacing={1}
            px={size === 'xs' ? 1.5 : 2}
            py={size === 'xs' ? 0 : 0.5}
            borderRadius="full"
            bgGradient={cfg.gradient}
            color="white"
            display="inline-flex"
            boxShadow="0 2px 8px rgba(128, 90, 213, 0.25)"
            flexShrink={0}
        >
            <Icon as={cfg.icon} boxSize={size === 'xs' ? 2.5 : 3} />
            <Text as="span" fontSize={size === 'xs' ? '9px' : '10px'} fontWeight="800" letterSpacing="0.06em">
                {label || cfg.label}
            </Text>
        </HStack>
    );
    return tooltip ? <Tooltip label={tooltip} hasArrow>{chip}</Tooltip> : chip;
});
AiChip.displayName = 'AiChip';

/** Gradient icon tile used in AI headers and KPI tiles. */
export const AiGlyph = memo(({ icon, gradient = 'linear(135deg, purple.500, pink.500)', size = 10, iconSize = 5, pulse = false }) => (
    <Box
        position="relative"
        w={size}
        h={size}
        borderRadius="xl"
        bgGradient={gradient}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 6px 18px rgba(128, 90, 213, 0.35)"
        flexShrink={0}
    >
        <Icon as={icon} color="white" boxSize={iconSize} />
        {pulse && (
            <Box position="absolute" top="-3px" right="-3px" w="9px" h="9px" borderRadius="full" bg="pink.400" borderWidth="2px" borderColor="white" />
        )}
    </Box>
));
AiGlyph.displayName = 'AiGlyph';

const MODELS = {
    rsf:       { name: 'Random Survival Forest', short: 'RSF',    icon: RulGaugeIcon,   desc: 'Predicts remaining useful life from condition, age, traffic and drainage features.' },
    iforest:   { name: 'Isolation Forest',       short: 'iForest',icon: AiAnomalyIcon,  desc: 'Flags unusual sensor or dashcam telemetry as anomalies.' },
    dbscan:    { name: 'Spatial Clustering',     short: 'DBSCAN', icon: AiClusterIcon,  desc: 'Groups nearby anomalies into actionable work orders.' },
    yolo:      { name: 'Dashcam Vision',         short: 'YOLOv8', icon: AiChipIcon,     desc: 'On-device pothole detection from the field app dashcam.' },
    insights:  { name: 'Insight Engine',         short: 'Insights', icon: AiNeuralIcon, desc: 'Turns predictions into prioritised, actionable recommendations.' },
};

/** Names the model behind a number or panel. */
export const ModelTag = memo(({ model = 'rsf' }) => {
    const m = MODELS[model] || MODELS.insights;
    const bg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const color = useColorModeValue('purple.700', 'purple.200');
    return (
        <Tooltip label={`${m.name} — ${m.desc}`} hasArrow>
            <HStack as="span" spacing={1} px={1.5} py={0.5} borderRadius="md" bg={bg} color={color} display="inline-flex">
                <Icon as={m.icon} boxSize={3} />
                <Text as="span" fontSize="9px" fontWeight="800" letterSpacing="0.04em">{m.short}</Text>
            </HStack>
        </Tooltip>
    );
});
ModelTag.displayName = 'ModelTag';

/** Compact confidence meter (accepts 0–1 or 0–100). */
export const ConfidenceMeter = memo(({ value, w = '60px' }) => {
    const track = useColorModeValue('gray.100', 'whiteAlpha.200');
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
    const pct = Math.round(Math.max(0, Math.min(100, Number(value) > 1 ? Number(value) : Number(value) * 100)));
    const color = pct >= 80 ? 'purple.500' : pct >= 60 ? 'blue.400' : 'gray.400';
    return (
        <Tooltip label={`Model confidence ${pct}%`} hasArrow>
            <HStack spacing={1.5} display="inline-flex">
                <Box w={w} h="5px" borderRadius="full" bg={track} overflow="hidden">
                    <Box h="100%" w={`${pct}%`} bg={color} borderRadius="full" transition="width 0.4s ease" />
                </Box>
                <Text fontSize="9px" fontWeight="800" color={color}>{pct}%</Text>
            </HStack>
        </Tooltip>
    );
});
ConfidenceMeter.displayName = 'ConfidenceMeter';
// --- END UPDATE
